import React from 'react'
import {Link as RouteLink} from 'react-router-dom'
import PropTypes from 'prop-types'

// Components
import {
    Grid,
    GridItem,
    Image,
    Link,
    Text,
    useBreakpointValue,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {LinkBox, LinkOverlay, Skeleton, UnorderedList, ListItem} from '@chakra-ui/react'

import {usePage} from '@salesforce/commerce-sdk-react'
import DynamicImage from '@app/components/dynamic-image'
import usePDCResponsiveElement from '../../hooks/use-pdc-responsive-element'
import {useUrlFromLink} from '../../page-designer/helpers/urlHelper'
import {DYNAMIC_IMAGE_WIDTHS} from '@app/constants.js'

// utils
import {getEnvStaleTime, getPageDesignerRegion} from '../../utils/utils'

/**
 * Page link component used in SEO Menu
 *
 * @param {object} props - props object
 * @param {string} props.pageId - the page id to link to
 */
export const PageLink = ({pageId, ...props}) => {
    const staleTime = getEnvStaleTime()
    const {data: page} = usePage({parameters: {pageId: pageId}}, {staleTime: staleTime})
    const url = useUrlFromLink({type: 'PAGE', target: page?.id})

    return (
        <Link href={url} {...props}>
            {page?.name}
        </Link>
    )
}

/**
 * Navigation menu level 2 Page Designer page component
 *
 * @param {object} props - props object
 * @param {string} props.pagedata - the page data to link to
 * @param {boolean} props.isOpen - is the drawer open
 * @returns {React.ReactElement}
 */
const DrawerMenuLvl2PDPage = ({pagedata, isOpen}) => {
    const {data: page, isLoading: loading} = usePage({parameters: {pageId: pagedata?.page}})
    const aboutStoryBlockDefine = getPageDesignerRegion(page, 'main')?.components?.find(
        (component) => component.typeId === 'commerce_layouts.pdcAboutStoryBlockDefine'
    )
    // Get current image based on breakpoint
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: aboutStoryBlockDefine?.data?.smallImage,
            mediumEl: aboutStoryBlockDefine?.data?.mediumImage,
            largeEl: aboutStoryBlockDefine?.data?.largeImage,
            xlargeEl: aboutStoryBlockDefine?.data?.xlargeImage,
            xxlargeEl: aboutStoryBlockDefine?.data?.xxlargeImage
        })
    )
    const url = useUrlFromLink({type: 'PAGE', target: page?.id})
    const styles = useStyleConfig('Link')

    return (
        <LinkBox as="article" textAlign="center" height={'100%'} pb={['10px', null, '14px']}>
            <Skeleton
                isLoaded={!loading && (currentImage || pagedata)}
                minHeight="200px"
                minWidth="100%"
                startColor="lightBlue"
                endColor="white"
            >
                {currentImage ? (
                    <DynamicImage
                        src={`${currentImage?.url}[?sw={width}]`}
                        widths={DYNAMIC_IMAGE_WIDTHS.thirdandfullonsmalldevice}
                        height={['240px', null, '210px']}
                        imageProps={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            loading: 'eager',
                            alt:
                                aboutStoryBlockDefine?.data?.imageAlt === 'empty'
                                    ? ''
                                    : aboutStoryBlockDefine?.data?.imageAlt ||
                                      aboutStoryBlockDefine?.data?.title,
                            title:
                                aboutStoryBlockDefine?.data?.imageTitle === 'empty'
                                    ? ''
                                    : aboutStoryBlockDefine?.data?.imageTitle ||
                                      aboutStoryBlockDefine?.data?.title
                        }}
                    />
                ) : (
                    <Image
                        src={pagedata?.image?.url}
                        alt={aboutStoryBlockDefine?.data?.title || pagedata?.title}
                        ignoreFallback
                        height={'auto'}
                        width={'100%'}
                    />
                )}
                <LinkOverlay
                    as={RouteLink}
                    to={url}
                    tabIndex={isOpen ? 0 : -1}
                    mt={['10px', null, '8px']}
                    display={'block'}
                    {...styles}
                    textDecoration={'none'}
                >
                    <Text
                        as="span"
                        fontFamily="heading"
                        textTransform={'uppercase'}
                        fontSize="12px"
                        lineHeight={1}
                        display={'block'}
                        color={
                            pagedata?.title && !aboutStoryBlockDefine?.data?.title
                                ? pagedata?.titleColor
                                : 'black'
                        }
                    >
                        {aboutStoryBlockDefine?.data?.title || pagedata?.title}
                    </Text>
                </LinkOverlay>
                {(aboutStoryBlockDefine?.data?.subTitle || pagedata?.subtitle) && (
                    <Text
                        variant="bodySmall"
                        color={
                            pagedata?.subtitle && !aboutStoryBlockDefine?.data?.subTitle
                                ? pagedata?.subtitleColor
                                : 'darkGray'
                        }
                        mt="2px"
                    >
                        {aboutStoryBlockDefine?.data?.subTitle || pagedata?.subtitle}
                    </Text>
                )}
            </Skeleton>
        </LinkBox>
    )
}

/**
 * Navigation menu level 2 Page Designer pages component
 *
 * @param {object} props - props object
 * @param {object} props.item - the item to display
 * @param {boolean} props.isOpen - is the drawer open
 * @returns {React.ReactElement}
 */
export const DrawerMenuLvl2PDPages = ({item, isOpen, ...props}) => {
    const pagesData =
        item.regions?.filter((region) => region.id === 'pdc_page_ids')[0]?.components || []

    return (
        <Grid as={UnorderedList} templateColumns={'repeat(2, 1fr)'} gap="10px" m={0} {...props}>
            {pagesData.map((pageData, i) => {
                return (
                    <GridItem
                        as={ListItem}
                        listStyleType="none"
                        key={pageData?.id}
                        colSpan={i % 3 === 2 ? 2 : 1}
                    >
                        <DrawerMenuLvl2PDPage pagedata={pageData?.data} isOpen={isOpen} />
                    </GridItem>
                )
            })}
        </Grid>
    )
}

DrawerMenuLvl2PDPages.propTypes = {
    item: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    isOpen: PropTypes.bool
}

PageLink.propTypes = {
    pageId: PropTypes.string.isRequired,
    props: PropTypes.object
}

DrawerMenuLvl2PDPage.propTypes = {
    pagedata: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    isOpen: PropTypes.bool
}

export default DrawerMenuLvl2PDPages
